import { useQuery } from '@tanstack/react-query'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { isSettingsPath, rememberLocation, settingsReturnTarget } from './settingsReturn'
import { EaseedeskSettingsMenu } from './EaseedeskSettingsMenu'
import { SettingsZone } from './SettingsRail'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { todayCountsQuery, type TodayCounts } from '../api/overview'
import { isFullscreen, isItem, isSpace, moduleOf, selectedNavItem, shellQuery, type NavItem, type ShellData } from '../api/shell'
import { settingsDrawerQuery } from '../api/settingsDrawer'
import { AccessCodeModal } from './AccessCodeModal'
import { Adam } from './Adam'
import { AdamDrawer } from '../ai/AdamDrawer'
import { IdleBar } from './IdleBar'
import { ChangeDrawer } from '../changes/ChangeDrawer'
import { ModuleBar } from './ModuleBar'
import { CollaborateBell } from './CollaborateBell'
import { NotificationsBell } from './NotificationsBell'
import { AgentReleasesMark } from './AgentReleasesMark'
import { TodoMenu } from './TodoMenu'
import { MemoBadge, MemoBoard } from './MemoBoard'
import { InflightBadges } from './InflightBadges'
import { InflightDrawer } from './InflightDrawer'
import { QuickSidebar } from '../easeedesk/QuickSidebar'
import { UserSidebar } from './UserSidebar'
import { SearchPalette } from './SearchPalette'
import { memoCount } from '../api/memo'
import { apiPost, signOut } from '../api/client'
import { readCookie } from './portalForm'
import { ShellSlotsContext, type ShellSlots } from './shellSlots'
import { AdamAccessContext } from './adamAccess'
import { isBarePage } from './barePages'
import { markNavigation, useHeaderNavReveal } from './useHeaderNavReveal'
import { assetUrl } from './assetUrl'
/* Regulile subsolului, copiate din `<style>`-ul lui `_layout/footer.php`:
   foile portalului nu le au. Vezi capul fisierului. */
import './Shell.css'
import './HeaderModules.css'

/**
 * Scheletul paginii, cu markupul din application/views/version-3/_layout/header.php
 * si footer.php, element cu element, ca foile portalului sa deseneze la fel:
 * `#page-header` (sigla, numele modulului, Today, grila de module, numele
 * sectiunii, meniul de firma, cautarea, actiunile rapide, Settings, bara
 * laterala), `#header-modules`, `#page-header-nav`, `#page-content`,
 * `#pageFooter`.
 *
 * Ce difera fata de portal, si nu se vede: bara modulului si grila se poarta
 * din useHeaderNavReveal (nu jQuery), iar uneltele listelor se deseneaza
 * direct la locul lor (vezi shellSlots.ts), nu se muta dupa desenare.
 */

/** Adresa din portal a unei intrari (`inventoree/workstations`) -> ruta din /app, daca e mutata. */
/*
 * Bara laterala din dreapta (`#user-sidebar`) e `UserSidebar.tsx`: filele
 * My Tickets si Commands, cu aceleasi capete ca in portal. Notifications,
 * Collaborate si Memo au ramas in antet, Chat vine cu ecranul de suport.
 *
 * `user-sidebar-active` pe <body> aduce panoul la `right: 0` (app.css:21812) SI
 * ingusteaza `#main-content` cu 320px (app.css:34193); portalul lasa cookie-ul
 * `user_sidebar=visible` cand omul si-a deschis bara, si se respecta.
 */
const SIDEBAR_READY = true

/**
 * `od-console` - clasa de pe care porneste TOATA foaia `od_console.css`.
 *
 * Foaia aia (`public/css/od_console.css`, in PORTAL_STYLES din `vite.config.ts`)
 * imbraca butonul, campul, bifa, cardul, pastila, tabelul si modalul pe desenul
 * consolei, si fiecare regula din ea incepe cu `body.od-console`. Portalul o
 * pune din `header.php`:
 *
 *     <body class="<?= $odModule ?> <?= od_console_class($odModule) ?> ...">
 *
 * iar `od_console_class()` (general_helper) o da modulelor din
 * `od_console_zones()` - azi doua: `settings` si `feedback`.
 *
 * IN v4 NU O PUNEA NIMENI. Scheletul scria pe `<body>` doar numele modulului,
 * deci intreaga foaie era incarcata si INERTA pe ecranele rescrise, iar fiecare
 * lucru pe care ea il indreapta se intorcea unul cate unul ca defect:
 *
 *   - bifa cu `form-control` pe ea (Bootstrap ii da `display: block` si
 *     `height: 34px`) ramanea o casuta inalta cat un camp de text, cu eticheta
 *     cazuta dedesubt - „IT Manager" din fisa contului, 10 septembrie 2026;
 *   - `--odc-pill-bg` nu exista, deci randul pe care STAI din meniurile de modul
 *     nu se vedea ca ales, si a primit o valoare de rezerva in
 *     `settings_console.css`, scoasa acum.
 *
 * ADRESA, nu doar modulul: in portal, jurnalul (`Change_log`) isi pune
 * `appModule = 'change log'`, care NU e in `od_console_zones()`, iar
 * `Product_feedback` isi pune `feedback`, care e. In v4 amandoua cad pe modulul
 * `settings` (`MODULE_BY_FIRST_SEGMENT`), deci `change_log` se scade inapoi de
 * mana ca sa ramana ca in portal.
 */
const OD_CONSOLE = 'od-console'

const MOVED: Record<string, string> = {
  'settings/licenses/companies_support': '/settings/licenses/companies_support',
  'settings/licenses/owners_support': '/settings/licenses/owners_support',
  dashboard: '/dashboard',
  /* Rapoartele dinamice, din sertarul rotitei (General): zona e numai in v4. */
  reports: '/reports',
  /*
   * Tabloul de modul are ruta lui in /app de pe 9 septembrie 2026
   * (`inventoreeDashboardRoute`), dar lipsea de aici - deci intrarea de meniu
   * pleca spre portalul vechi, care o intorcea incoace. Drumul dus-intors se
   * vedea ca o pagina care "sare" in lista de statii.
   */
  'inventoree/dashboard': '/inventoree/dashboard',
  'settings/audit': '/settings/audit',
  'inventoree/workstations': '/inventoree/workstations',
  'inventoree/servers': '/inventoree/servers',
  'inventoree/inactive': '/inventoree/inactive',
  'inventoree/decommissioned': '/inventoree/decommissioned',
  'inventoree/mobiles': '/inventoree/mobiles',
  'inventoree/unmanaged': '/inventoree/unmanaged',
  'inventoree/network': '/inventoree/network',
  'inventoree/agentless': '/inventoree/agentless',
  'inventoree/printers': '/inventoree/printers',
  'inventoree/reports': '/inventoree/reports',
  'inventoree/alarms': '/inventoree/alarms',
  'inventoree/live_devices': '/inventoree/live_devices',
  'inventoree/software_licenses': '/inventoree/software_licenses',
  'inventoree/unassigned': '/inventoree/unassigned',
  'inventoree/inventory_map': '/inventoree/inventory_map',
  'inventoree/live_agentbased': '/inventoree/live_agentbased',
  'inventoree/endpoint_performance_dashboard': '/inventoree/endpoint_performance_dashboard',
  'opteemum/alarms': '/opteemum/alarms',
  'proactivitee/alarms': '/proactivitee/alarms',
  'proactivitee/all_devices': '/proactivitee/all_devices',
  'proactivitee/workstations': '/proactivitee/workstations',
  'proactivitee/servers': '/proactivitee/servers',
  'proactivitee/network_devices': '/proactivitee/network_devices',
  'proactivitee/mobiles': '/proactivitee/mobiles',
  'proactivitee/extended_monitoring': '/proactivitee/extended_monitoring',
  'proactivitee/device': '/proactivitee/device',
  'proactivitee/snmp_dashboard': '/proactivitee/snmp_dashboard',
  'dashboards/onsite_presence': '/dashboards/onsite_presence',
  'proactivitee/snmp_services': '/proactivitee/snmp_services',
  'proactivitee/snmp_host_monitoring': '/proactivitee/snmp_host_monitoring',
  'proactivitee/core_applications': '/proactivitee/core_applications',
  'proactivitee/core_applications_dashboard': '/proactivitee/core_applications_dashboard',
  'proactivitee/applications_dashboard': '/proactivitee/applications_dashboard',
  'securitee/alarms': '/securitee/alarms',
  /* Securitee a intrat in /app pe 12 septembrie 2026 (All Devices, Events,
     tabloul de modul); pana atunci bara lui trimitea 3 din 4 intrari la
     portalul vechi. */
  'securitee/all_devices': '/securitee/all_devices',
  'securitee/events': '/securitee/events',
  'securitee/incidents': '/securitee/incidents',
  'securitee/detections': '/securitee/detections',
  'securitee/edr_events': '/securitee/edr-events',
  'securitee/gravityzone': '/securitee/gravityzone',
  'securitee/ad-accounts': '/securitee/ad-accounts',
  'securitee/email-domains': '/securitee/email-domains',
  'securitee/network-checks': '/securitee/network-checks',
  'securitee/dashboard': '/securitee/dashboard',
  /* Protectee a intrat in /app pe 12 septembrie 2026: tabloul, All Devices, si
     legatura `protectee/deviceBackupEvents/` din System Alarms (CoverageEngine),
     care duce la lista - fila Backup events e in randul desfasurat. */
  'protectee/dashboard': '/protectee/dashboard',
  'protectee/all_devices': '/protectee/all_devices',
  protectee: '/protectee/dashboard',
  /* Printeeng a intrat in /app pe 12 septembrie 2026. */
  'printeeng/dashboard': '/printeeng/dashboard',
  'printeeng/all_devices': '/printeeng/all_devices',
  'printeeng/printers': '/printeeng/printers',
  'printeeng/events': '/printeeng/events',
  printeeng: '/printeeng/dashboard',
  /* Saveenergee a intrat in /app pe 13 septembrie 2026. */
  'saveenergee/dashboard': '/saveenergee/dashboard',
  'saveenergee/all_devices': '/saveenergee/all_devices',
  'saveenergee/events': '/saveenergee/events',
  saveenergee: '/saveenergee/dashboard',
  /* Productivitee a intrat in /app pe 13 septembrie 2026. */
  'productivitee/dashboard': '/productivitee/dashboard',
  'productivitee/device_productivity': '/productivitee/device_productivity',
  'productivitee/user_productivity': '/productivitee/user_productivity',
  'productivitee/user_productivity_period': '/productivitee/user_productivity_period',
  /* Tabloul de conducere, 21 septembrie 2026 - ecran al lui v4, fara pereche in portalul vechi. */
  'productivitee/management': '/productivitee/management',
  productivitee: '/productivitee/dashboard',
  'settings/inventoree': '/settings/inventoree',
  /*
   * Aceeasi scapare ca la Inventoree, cu o zi mai tarziu: livrarea din 10
   * septembrie 2026 a dat tablourilor de modul Softsee, Opteemum si
   * Proactivitee ruta lor in /app (`softseeDashboardRoute` si perechile ei)
   * si capatul comun `GET /api/v2/{module}/board`, dar nu si randul de aici.
   * Fara el `navUrl()` cade pe `portalUrl()`, deci "Dashboard" din bara
   * fiecarui modul pleca spre portalul vechi - care nu mai e pe server. Nu
   * lipsea nici ecranul, nici ruta, ci INTRAREA DE MENIU.
   *
   * Adresa e aceeasi de amandoua partile, dinadins: un semn de carte vechi si
   * o legatura dintr-un tichet ajung tot aici.
   */
  'softsee/dashboard': '/softsee/dashboard',
  'opteemum/dashboard': '/opteemum/dashboard',
  'proactivitee/dashboard': '/proactivitee/dashboard',
  'softsee/all_devices': '/softsee/all_devices',
  'softsee/workstations': '/softsee/workstations',
  'softsee/servers': '/softsee/servers',
  'softsee/mobiles': '/softsee/mobiles',
  'softsee/applications': '/softsee/applications',
  'softsee/licenses': '/softsee/licenses',
  'softsee/certificates': '/softsee/certificates',
  /* Procedures a trecut din bara Softsee in Settings > Softsee (cec-master aa267b09e). */
  'softsee/procedures': '/settings/softsee/procedures',
  'softsee/installMonitor': '/softsee/installMonitor',
  /*
   * „ADM Map" e o intrare in bara Softsee (`Shell::softseeNav()` scrie
   * `adm/map`), deci fara randul asta `navUrl()` ar lasa-o la portalul vechi -
   * care nu mai e pe server. Adresa e aceeasi de amandoua partile, dinadins:
   * si `adm/map?root=<id>`, scris din tichete, din registru si din catalogul de
   * servicii, ajunge tot aici.
   */
  'adm/map': '/adm/map',
  'settings/softsee': '/settings/softsee',
  /*
   * Settings > Securitee / Printeeng, surorile Softsee (13 septembrie 2026):
   * `securiteeSettingsTabRoute`, `printeengSettingsTabRoute`. Filele
   * vechi (`settings/securitee/cryptolocker_protection` s.a.) sunt file si
   * aici, la aceeasi adresa, prin hub-ul din `V4_SETTINGS`.
   */
  'settings/securitee': '/settings/securitee',
  'settings/printeeng': '/settings/printeeng',
  /* Settings > Protectee (13 septembrie 2026): `protecteeSettingsTabRoute`. */
  'settings/protectee': '/settings/protectee',
  'settings/saveenergee': '/settings/saveenergee',
  'settings/productivitee': '/settings/productivitee',
  'opteemum/workstations': '/opteemum/workstations',
  'opteemum/servers': '/opteemum/servers',
  'opteemum/mobiles': '/opteemum/mobiles',
  'opteemum/inactive': '/opteemum/inactive',
  'opteemum/live_devices': '/opteemum/live_devices',
  'opteemum/unassigned': '/opteemum/unassigned',
  'opteemum/applications': '/opteemum/applications',
  'opteemum/schedule_management': '/opteemum/schedule_management',
  'opteemum/remote_connect': '/opteemum/remote_connect',
  /*
   * Lansarea RustDesk (`Remote_connect::rustdesk_launch`), mutata pe 11
   * septembrie 2026 - `rustdeskLaunchRoute`. Nicio bara n-o scrie: se ajunge
   * la ea din butonul Connect al tichetului, cu numarul masinii si al
   * tichetului in adresa. Randul sta aici oricum, in ACELASI commit cu ruta:
   * `MOVED` e registrul a ce s-a mutat, si o legatura veche spre adresa asta
   * trebuie sa duca in ecranul care exista, nu la portalul plecat de pe server.
   */
  'remote_connect/rustdesk_launch': '/remote_connect/rustdesk_launch',
  'settings/opteemum': '/settings/opteemum',
  'settings/diagnostic_reports': '/settings/diagnostic_reports/computer_scan',
  'settings/proactivitee': '/settings/proactivitee',
  /*
   * Policies > Alarms. `settings/alarms_new/proactivitee` era o a doua fata,
   * pe categorii, a ACELORASI randuri din `it_opteemum_alerts_settings` pe
   * care le editeaza Settings > Proactivitee > Alarms (`LiveAlarmsEditor`) -
   * si o fata ramasa in urma: butoanele ei de creare, stergere si comutare
   * trimiteau la `settings/#` (nicaieri), iar doua din cele trei machete de
   * detaliu lipsesc de pe disc (`tools/check_loads_known.txt`). Intrarea din
   * rail duce deci in editorul viu.
   *
   * Dar pe adresa ZONEI, nu pe a modulului. Pana pe 7 octombrie 2026 tinta
   * era `/settings/proactivitee/alarms`, si fiindca Alarms e prima intrare
   * Policies, clicul pe „Policies" din bara ajungea in setarile Proactivitee:
   * meniul din stanga al modulului, Proactivitee aprins in bara, zona pierduta.
   * `/settings/alarms_new/proactivitee` deseneaza acelasi editor sub rail-ul
   * Policies (`match` pe `settings/alarms_new`) - `settingsZoneLanding.test.ts`.
   */
  'settings/alarms_new': '/settings/alarms_new/proactivitee',
  'settings/alarms_new/proactivitee': '/settings/alarms_new/proactivitee',
  'settings/diagnostic': '/settings/diagnostic/actions',

  /*
   * PATRATELUL „Settings" DIN MENIUL DE MODULE.
   *
   * Adresa lui nu e scrisa in cod: vine din `it_modules.url`, si acolo scrie
   * `settings` - atat, fara a doua bucata. Celelalte patratele poarta adrese
   * intregi (`inventoree/dashboard`, `softsee/dashboard`), au randul lor aici,
   * si duc in /app. Al Setarilor n-avea rand, deci `navUrl()` cadea pe
   * `portalUrl()` si patratelul pleca spre `/settings` din portalul vechi -
   * care nu mai sta pe server. Adica singura usa dintre module catre setari
   * ducea in gol.
   *
   * Tinta: pagina de hub `/settings` a portalului (`settings/All_settings::
   * index`, directorul tuturor ecranelor de setari), mutata in v4 pe 12
   * septembrie 2026 (`SettingsDirectoryPage`). Pana atunci patratelul ducea
   * la `General`, prima fila.
   *
   * Randul asta a stat o livrare si n-a mers: pe server `it_modules.url` NU
   * e `settings` dintr-o bucata, cum spunea inregistrarea din teste, si o
   * potrivire exacta pe o valoare pe care n-o scrie nimeni in cod nu prinde
   * nimic. Patratelul isi ia acum tinta dupa SLUG, in `HeaderModules` - vezi
   * `SETTINGS_TILE` - iar randul ramane pentru cine chiar cere `settings`.
   */
  settings: '/settings',

  /*
   * HUB-URILE CARE N-AU PAGINA DE HUB, CI NUMAI ECRANE.
   *
   * `settingsUrl()` reduce o adresa de setari la hub-ul ei, si pentru cele mai
   * multe asta e chiar pagina care exista (`/settings/companies`). Pentru astea
   * trei nu: in v4 s-au mutat ECRANELE, nu si pagina care le aduna. Un hub
   * scris in `V4_SETTINGS` fara o tinta aici ar duce la ecranul de "nu exista",
   * adica mai rau decat portalul mort de dinainte.
   *
   * Tinta nu se alege din burta: e chiar prima fila a hub-ului din portal -
   * `easeedesk_settings/index.php` deschide `ticket_types_settings`, la fel cum
   * `diagnostic_reports` deschide `computer_scan` mai sus.
   */
  'settings/easeedesk': '/settings/easeedesk/ticket_types_settings',
  'settings/company_reports': '/settings/company_reports/index',
  'settings/customersV2': '/settings/customersV2/contracts',

  /*
   * Doua adrese pe care reducerea la hub le-ar duce in locul GRESIT, nu intr-un
   * loc lipsa - de-aia se scriu intregi si se caută intregi (vezi `settingsUrl`).
   *
   * `settings/ai_agent` si `settings/ai_agent/company` sunt DOUA ecrane: unul e
   * agentul furnizorului, celalalt al firmei. Reduse amandoua la `ai_agent`,
   * randul din zona firmei ar deschide ecranul global.
   *
   * `services_providers/service_providers` nu sta deloc sub `settings/`, deci
   * `settingsUrl` nu-i gaseste niciun hub si cadea pe adresa portalului. Randul
   * il scrie `Services\Navigation\SettingsMenu` („Service Providers", zona
   * Global), iar ecranul exista in v4 de la 26.9.10.1.
   */
  'settings/ai_agent/company': '/settings/ai_agent/company',
  /* IT Audit, registrul de constatari - ecranul lui, nu hub-ul. */
  'settings/audit/register': '/settings/audit/register',
  /* IT Audit, documentul de predat - `paper/$id`; PDF-ul se cere din pagina. */
  'settings/audit/paper': '/settings/audit/paper',
  'settings/audit/snapshot': '/settings/audit/snapshot',
  'settings/audit/ask': '/settings/audit/ask',
  /*
   * Briefingul fara tip = toate auditurile (0). Pana la 26.9.15.52 tinta era
   * `/settings/audit/prompts/0`, fiindca adresa fara numar n-avea ruta; de
   * atunci `settingsAuditPromptsAllRoute` o are, cu acelasi ecran, deci
   * legatura duce la adresa ei, ca in portal.
   */
  'settings/audit/prompts': '/settings/audit/prompts',
  'services_providers/service_providers': '/services_providers/service_providers',
  /*
   * Catalogul de servicii. Intrarea de meniu e in OVERVIEW - `Shell::overviewNav`
   * o scrie cu `href: 'service_catalog'`, ca in `header_menu/overview.php` - dar
   * ecranul e la radacina, ca in portal, si poarta banda Easeedesk. Fara randul
   * asta intrarea pleca spre portalul vechi, care nu mai e pe server.
   */
  service_catalog: '/service_catalog',
  /* Aplicatiile de business - intrarea "Applications" din Overview. */
  applications: '/applications',

  /*
   * ADM / Changes si CMDB - mutate in /app pe 10 septembrie 2026
   * (`admChangesRoute`, `admChangesScopeRoute`, ruta `/cmdb`), fara randurile
   * de aici. Aceeasi scapare ca la tablourile de modul, si tot atat de greu de
   * vazut: ecranul exista, ruta exista, si intrarea de meniu pleaca spre
   * portalul care nu mai e pe server.
   *
   * `adm/changes` fara ingustare e usa din Overview („All Changes"); cele patru
   * cu ingustare sunt cate una din bara fiecarui modul care are ce arata, si
   * sunt EXACT `CHANGE_SCOPES` din `api/admChanges.ts`. Se scriu intregi,
   * fiindca ruta lor e cu parametru (`/adm/changes/$scope`) si `MOVED` se
   * cauta pe adresa intreaga.
   */
  cmdb: '/cmdb',
  /* Intrarea in masa dintr-o foaie - `cmdbImportRoute`. Randul asta pleaca in
     ACELASI commit cu ruta: fara el, „Import" din lista CMDB ar duce la
     portalul vechi, care nu mai sta pe server. */
  'cmdb/import': '/cmdb/import',
  /*
   * Ferestrele de mentenanta - a patra fila din banda CMDB, si singura usa
   * catre ecran: in portal nu exista nicio intrare de meniu catre
   * `/maintenance`, se ajunge acolo numai din banda. Randul e aici fiindca
   * `MOVED` e registrul a ce s-a mutat, nu doar tabelul barelor - iar cine
   * scrie maine o intrare de meniu spre `maintenance` o gaseste gata mutata.
   */
  maintenance: '/maintenance',
  'maintenance/calendar': '/maintenance/calendar',
  'maintenance/day': '/maintenance/day',
  change: '/change',
  'adm/dashboard': '/adm/dashboard',
  /*
   * Erorile si acoperirea, mutate pe 10 septembrie 2026. Bara de modul nu le
   * scrie astazi - la erori se ajunge din banda celor cinci vederi, la
   * acoperire din cartonasele tabloului, si amandoua deseneaza `<a href>`
   * intregi - dar randurile stau aici de la inceput: in ziua in care cineva
   * pune intrarea in meniu, ea trebuie sa duca in /app, nu spre portalul care
   * nu mai e pe server. Asa s-a pierdut de trei ori un ecran gata scris.
   */
  'adm/errors': '/adm/errors',
  'adm/coverage': '/adm/coverage',
  /* Scanarile si programarile. Nu sunt in nicio bara de modul - se ajunge la
     ele din banda de vederi a descoperirii (`_views.php`) - dar randurile stau
     aici oricum: `navUrl()` e singurul loc care stie ce s-a mutat, si o adresa
     de ADM care ii scapa pleaca spre portalul care nu mai e pe server. */
  'adm/runs': '/adm/runs',
  'adm/schedules': '/adm/schedules',
  'adm/changes': '/adm/changes',
  'adm/changes/hardware': '/adm/changes/hardware',
  'adm/changes/software': '/adm/changes/software',
  'adm/changes/opteemum': '/adm/changes/opteemum',
  'adm/changes/proactivitee': '/adm/changes/proactivitee',
  /* Coada de revizie din bara Softsee - `header_menu/softsee.php`. */
  'adm/pending': '/adm/pending',

  /*
   * Coada de PROGRAME si podul de categorii, mutate pe 10 septembrie 2026.
   *
   * `Shell::softseeNav()` scrie „ADM Software Review" in bara, sub acelasi
   * `softsee.adm_pending.read` ca si coada de aparate. Podul de categorii nu e
   * in bara - la el se ajunge din coada de programe - dar randul lui sta aici
   * fiindca adresa portalului mai apare in legaturi vechi, si ea trebuie sa
   * duca in ecranul care exista, nu la un server pe care portalul nu mai sta.
   */
  'adm/softwarePending': '/adm/softwarePending',
  'adm/softwareCategories': '/adm/softwareCategories',
  'overview/today': '/overview/today',
  'overview/ahead': '/overview/ahead',
  'overview/dashboard': '/overview/dashboard',
  'overview/nis2': '/overview/nis2',
  'overview/domains': '/overview/domains',
  /*
   * Problems - a treia intrare Overview mutata, dupa CMDB si All Changes.
   *
   * Randul asta pleaca in ACELASI commit cu ruta. Fara el, ecranul exista in
   * /app si bara trimite mai departe la `/problems` din portalul vechi, care
   * NU mai sta pe server: legatura nu deschide o versiune mai veche a
   * ecranului, ci nimic - adica, pentru cine foloseste produsul, Problems nu
   * exista. `moduleBarLinks.test.ts` pica exact pe cazul asta.
   */
  problems: '/problems',

  /*
   * Easeedesk, cele unsprezece ecrane care au ecran in v4.
   *
   * Lista de aici o oglindeste pe `Application\Services\V4\EaseedeskCut::PAGES`
   * si NU e mai lunga decat ea. Modulul se muta pe bucati: ce nu e aici -
   * feedbackul, paginile din emailurile deja trimise, `my_new_performance` -
   * ramane pe drumul vechi, si bara trebuie sa duca acolo, nu intr-un ecran
   * care inca nu exista.
   *
   * `easeedesk/ticket` nu e in lista: e o adresa cu numar (`ticket/4711`), si
   * bara n-o scrie niciodata fara el.
   */
  'easeedesk/tickets': '/easeedesk/tickets',
  'easeedesk/closed_tickets': '/easeedesk/closed_tickets',
  'easeedesk/tickets_dashboard': '/easeedesk/tickets_dashboard',
  'easeedesk/dashboard': '/easeedesk/dashboard',
  'easeedesk/all_reports': '/easeedesk/all_reports',
  'easeedesk/my_performance': '/easeedesk/my_performance',
  'easeedesk/oncall': '/easeedesk/oncall',
  'easeedesk/customers': '/easeedesk/customers',
  'easeedesk/gps_tracking': '/easeedesk/gps_tracking',
  'easeedesk/it_solutions': '/easeedesk/it_solutions',
  /* Biblioteca furnizorului, deschisa din "Master Edit" de pe IT Solutions. */
  'solutions/categories': '/solutions/categories',
  'easeedesk/it_policies': '/easeedesk/it_policies',

  /*
   * Cele doua usi ale clientului: „Self Service" si „Service Shop".
   *
   * Nu stau sub `easeedesk/` nici in portal - sunt controllere de radacina - si
   * exact de-aia s-au pierdut: cine a cautat aici randurile Easeedesk s-a uitat
   * dupa prefix, iar astea n-au niciunul. Ecranele sunt scrise
   * (`SelfServicePage`, `ServiceShopPage`), rutele sunt in arbore de la
   * `easeedeskSelfServiceRoute` incoace, si `moduleOf` le da chiar bara
   * Easeedesk - numai randul de aici lipsea, deci amandoua intrarile de meniu
   * plecau spre portalul care nu mai e pe server.
   *
   * Filele lor de dedesubt (`self_service/searches`, `service_shop/requests`)
   * nu se scriu niciodata de bara, deci nu au rand: se ajunge la ele dinauntru,
   * prin router.
   */
  self_service: '/self_service',
  service_shop: '/service_shop',
}

/**
 * Insigna de pe "Change Log" din subsol: cate livrari calendar n-a deschis omul.
 *
 * Transcrierea `<script>`-ului de la capatul lui `_layout/footer.php`, rand cu
 * rand: se numara versiunile pana la cea tinuta in `localStorage`, sub cheia
 * portalului (`od-cl-seen-{uid}`), deci cine a citit jurnalul acolo nu-l
 * gaseste tot nou aici. Peste 99 scrie "99+"; pe ecranul jurnalului insusi
 * insigna se stinge - te uiti chiar la ea.
 *
 * `/change_log` prinde AMANDOUA usile jurnalului: cea scurta, `/app/change_log`,
 * pe care o scrie chiar legatura asta, si cea lunga din rotita,
 * `/app/settings/change_log`. Se uita la aceeasi bucata de adresa, deci nu
 * trebuie tinuta la zi cand se muta o usa.
 */
export function changeLogUnseen(versions: string[] | undefined, viewer: number, pathname: string): string {
  if (pathname.includes('/change_log')) return ''

  let seen = ''
  try {
    seen = localStorage.getItem('od-cl-seen-' + String(viewer || 0)) ?? ''
  } catch {
    /* Un browser care nu tine minte: totul e "nou", ca in portal. */
  }

  let n = 0
  for (const version of versions ?? []) {
    if (version === seen) break
    n++
  }

  if (n < 1) return ''
  return n > 99 ? '99+' : String(n)
}

/** Insigna Today, ca od_ux.js: 99+, ascunsa pe zero. */
function todayBadge(counts?: { total?: number } | null): string {
  const total = Number(counts?.total) || 0
  if (total < 1) return ''
  return total > 99 ? '99+' : String(total)
}

function todayTitle(counts?: TodayCounts['counts'] | null): string {
  if (!counts) return 'What is waiting for you right now'
  const parts: string[] = []
  const rows: [Exclude<keyof TodayCounts['counts'], 'total'>, string, string][] = [
    ['alarms', 'open alarm', 'open alarms'],
    ['tickets', 'ticket', 'tickets'],
    ['changes', 'unauthorised change', 'unauthorised changes'],
    ['silent', 'agent gone quiet', 'agents gone quiet'],
    ['discovered', 'device found on the network', 'devices found on the network'],
  ]
  for (const [key, one, many] of rows) {
    const n = counts[key]
    if (n === null || n === undefined) continue
    const value = Number(n) || 0
    if (value > 0) parts.push(`${value} ${value === 1 ? one : many}`)
  }
  return parts.length ? parts.join(', ') : 'What is waiting for you right now'
}

/** Numele modulului din antet, ca in header.php: `ucfirst(str_replace('_', ' ', $appModule))`. */
function moduleName(module: string): string {
  const spaced = module.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function portalUrl(href: string): string {
  const clean = href.replace(/^\//, '')

  /*
   * Gol NU inseamna radacina.
   *
   * `/` e prinsa de ruta de start, care trimite mai departe in
   * `/inventoree/workstations`. Deci o intrare de meniu careia serverul nu i-a
   * dat adresa - o zona din care omul nu poate deschide nimic - ajungea sa
   * duca in lista de statii, ca si cum ar fi fost apasata din greseala. Mai
   * bine ramane la lista de setari, unde macar scrie ce e.
   */
  return clean === '' ? '/settings' : '/' + clean
}

/**
 * Adresa scrisa pe o intrare de meniu.
 *
 * Clicul obisnuit e prins mai jos (`onNavClick`) si trece prin router, deci
 * adresa asta conteaza cand omul deschide altfel: rotita, Ctrl+clic, "deschide
 * in fila noua". Pentru o pagina MUTATA trebuie sa fie deja cea din /app -
 * altfel deschiderea iese in portalul vechi, care o intoarce cu 302 tot aici,
 * si o fila noua se incarca de doua ori degeaba. Ce n-a plecat din CI3 ramane
 * adresa portalului, fiindca acolo chiar traieste ecranul.
 */
export function navUrl(href: string): string {
  const moved = MOVED[href]
  if (moved !== undefined) {
    return '/app' + moved
  }

  /*
   * O adresa de SETARI se intreaba la `settingsUrl()`, nu se lasa la portal.
   *
   * Bara de sub antet a zonei de Settings (14 septembrie 2026, `settingsNav()`)
   * scrie ca `href` al fiecarei zone PRIMUL ei ecran: `settings/general`
   * pentru Global, `settings/companies` pentru General - si `settings/<modul>`
   * pentru fiecare modul. `MOVED` nu le are pe toate, fiindca ecranele de
   * setari nu se scriu acolo, ci in `V4_SETTINGS`, unde se uita numai
   * `settingsUrl()`. Deci General pleca in portalul CodeIgniter, care nu mai
   * sta pe server, iar Global si Policies duceau in /app - trei usi una langa
   * alta, una dintre ele in gol.
   */
  if (href.startsWith('settings/')) {
    return settingsUrl(href)
  }

  return portalUrl(href)
}

/**
 * Schimbarea clientului si a sediului, apoi reincarcare.
 *
 * Chema `optimumdesk/change_company` din portal, care nu mai sta pe server:
 * apasarea pleca in gol si pagina se reincarca la fel cum era. Acum e un capat
 * al lui v4 (`App\Controller\CompanyController`), care CANTARESTE alegerea -
 * firma trebuie sa fie a omului, sediul trebuie sa fie al firmei - scrie
 * `it_last_session` si rescrie cookie-ul semnat cu noile `co`/`office`.
 *
 * Reincarcarea e intreaga, ca in portal: firma aleasa intra in fiecare
 * interogare a paginii, deci nu e o bucata de ecran care se improspateaza, ci
 * tot ecranul.
 */
/** „Toate sediile". Zero e un sediu adevarat, deci nu poate insemna „niciunul". */
const ALL_OFFICES = -1

export async function changeCompany(company: number, office: number, all: boolean): Promise<void> {
  await apiPost('/company/switch', { company, office, all: all ? 1 : 0 })
  window.location.reload()
}

/** Un `li.dropdown` de Bootstrap 3, fara Bootstrap: clasa `open` pe parinte, inchis la clic afara. */
function useDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLLIElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (event: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('click', away)
    return () => document.removeEventListener('click', away)
  }, [open])
  return { ref, open, toggle: () => setOpen((v) => !v), close: () => setOpen(false) }
}

/**
 * SELECTORUL DE CLIENT SI SEDIU, din antet.
 *
 * Markupul e cel din `_partials/change_company_dropdown.php` si purtarea e cea
 * din `public/js/company_switcher.js`, rand cu rand - foile portalului
 * (`od_ux.css`, sectiunea 7) il deseneaza fara sa fie nevoie de nimic scris
 * aici.
 *
 * Aici erau pana acum DOUA `<select>`-uri suprapuse si o bifa: alegeai firma,
 * se reincarca pagina, apoi alegeai sediul, se reincarca iar. Portalul a
 * plecat de la asta pe 7 septembrie 2026, fiindca pe un furnizor cu douazeci
 * de clienti un `<select>` nu se poate cauta si nu incape pe ecran.
 *
 *   - clientii in stanga, cu o cautare deasupra; sediile clientului aratat in
 *     dreapta, cu "All Offices" primul;
 *   - TRECUT peste un client: i se deschid sediile. APASAT: se intra la el, pe
 *     toate sediile - aia e alegerea de noua ori din zece;
 *   - Enter in cautare: se intra la primul client ramas pe ecran;
 *   - cine are un singur client primeste NUMAI coloana sediilor: o lista de un
 *     rand cu numele lui nu e o alegere, e un chenar.
 *
 * `focusin` langa `mouseenter`: cine merge cu tastatura vede aceleasi sedii
 * cand ajunge pe rand, fara sa apese.
 */
function CompanyDropdown({ shell }: { shell: ShellData }) {
  const { ref: ddRef, open: ddOpen, toggle: ddToggle, close: ddClose } = useDropdown()
  const findRef = useRef<HTMLInputElement>(null)
  const [find, setFind] = useState('')
  /* Clientul ale carui sedii sunt desenate acum in dreapta - nu neaparat cel
     ales: se schimba la trecerea cu mouse-ul, fara sa se intre nicaieri. */
  const [shown, setShown] = useState(shell.company.id)

  const current = shell.company.id
  const officeNow = shell.officeId

  /* Panoul deschis pune cursorul in cautare - si o goleste, ca sa nu ramana
     filtrul de data trecuta peste lista. */
  useEffect(() => {
    if (!ddOpen) return
    setFind('')
    setShown(current)
    const at = window.setTimeout(() => findRef.current?.focus(), 60)
    return () => window.clearTimeout(at)
  }, [ddOpen, current])

  const go = (company: number, office: number) => void changeCompany(company, office, shell.allCompanies)

  const needle = find.trim().toLowerCase()
  const left = shell.companies.filter((c) => needle === '' || c.name.toLowerCase().includes(needle))
  const offices = shell.officesByCompany[String(shown)] ?? []
  const here = shown === current
  const shownName = shell.companies.find((c) => c.id === shown)?.name ?? ''
  const manyCompanies = shell.companies.length > 1

  return (
    <li id="toogle-change-company" className={ddOpen ? 'dropdown open' : 'dropdown'} ref={ddRef}>
      <div data-toggle="dropdown" onClick={ddToggle}>
        {/*
          CHENARUL SE DESENEAZA NUMAI CAND ARE CE TINE.

          `#company-thumb-small` e o cutie de 34x34 cu fundal ALB si chenar alb
          (app.css:17127). Cu sigla inauntru e sigla clientului; goala, e un
          dreptunghi alb lipit de nume - exact ce s-a vazut in fotografia din 9
          septembrie 2026, cat timp adresa siglei venea goala din jeton.

          Deci: fara adresa, nu se deseneaza chenarul deloc. Iar daca serverul
          de descarcari nu intoarce imaginea, se scoate TOT chenarul, nu doar
          `<img>`-ul ca in macheta veche - acolo pierderea unei sigle era o
          intamplare rara pe o cutie care oricum avea alta, aici ar lasa in
          urma chiar dreptunghiul alb pe care il reparam.

          `{' '}` dupa chenar: macheta veche are un rand nou intre cele doua
          `<span>`-uri, si HTML il preface intr-un spatiu - JSX il arunca.
        */}
        {shell.company.logo !== '' && (
          <>
            <span id="company-thumb-small">
              <img
                src={shell.company.logo}
                onError={(e) => e.currentTarget.closest('#company-thumb-small')?.remove()}
                alt=""
              />
            </span>{' '}
          </>
        )}
        <span className="company-name hidden-md hidden-sm hidden-xs">
          {shell.company.name} <i className="fa fa-chevron-down"></i>
        </span>
        {/*
          "ALL COMPANIES" SE VEDE DIN ANTET, NU DOAR DIN BIFA DE JOS.

          Cu bifa pusa, sesiunea se uita la TOATE firmele omului, dar `company`
          ramane firma propusa la creare - deci antetul arata numele unei firme
          si omul crede ca e pe ea. Pe 30 octombrie 2026 autoevaluarea DNSC
          spunea "choose one company" cuiva care vedea deja o firma aleasa in
          antet: era pe toate (prima intrare, fara rand in `it_last_session`,
          porneste pe toate, ca in portal). Semnul sta in afara
          `.company-name`, ca sa ramana si pe ecranele inguste.
        */}
        {/*
          CU O SINGURA FIRMA NU EXISTA "TOATE FIRMELE".

          Toate firmele unui om cu o firma sunt chiar firma din antet: semnul
          si bifa de jos n-au ce sa schimbe, iar eticheta portocalie il trimitea
          sa debifeze ceva fara efect (Renania Trade SRL, 30 octombrie 2026).
          Se numara din lista pe care antetul o are deja (`shell.companies`),
          aceeasi dupa care se arata cautarea de clienti.
        */}
        {manyCompanies && shell.allCompanies && (
          <span className="od-switch-allmark" id="company-all-mark" title="All companies is on: untick it at the bottom of this menu to work on one company">
            All companies
          </span>
        )}
      </div>
      <div id="change-company-dropdown" className="dropdown-menu dropdown-menu-right p-0">
        <div className="od-switch bg-module">
          {manyCompanies && (
            <div className="od-switch-side">
              <div className="od-switch-find">
                <i className="fas fa-search" aria-hidden="true"></i>
                <input
                  type="search"
                  id="odSwitchFind"
                  ref={findRef}
                  placeholder="Search client…"
                  autoComplete="off"
                  aria-label="Search client"
                  value={find}
                  onChange={(e) => setFind(e.target.value)}
                  onKeyDown={(e) => {
                    /* Enter: se INTRA la primul client ramas pe ecran, pe toate
                       sediile. Asa se ajunge la un client scriind doua litere,
                       fara mouse. */
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    const first = left[0]
                    if (first) go(first.id, ALL_OFFICES)
                  }}
                />
              </div>
              <div className="od-switch-list" id="odSwitchClients">
                {left.map((company) => (
                  <button
                    type="button"
                    key={company.id}
                    className={'od-switch-row od-switch-client' + (company.id === current ? ' is-on' : '')}
                    data-company={company.id}
                    title={company.name + ' - all offices'}
                    onMouseEnter={() => setShown(company.id)}
                    onFocus={() => setShown(company.id)}
                    onClick={() => go(company.id, ALL_OFFICES)}
                  >
                    <span className="od-switch-logo">
                      {company.logo !== '' && (
                        <img src={company.logo} onError={(e) => e.currentTarget.remove()} alt="" />
                      )}
                    </span>
                    <span className="od-switch-name">{company.name}</span>
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                  </button>
                ))}
              </div>
              <p className="od-switch-none" id="odSwitchNone" hidden={left.length > 0}>
                No client by that name.
              </p>
            </div>
          )}

          <div className="od-switch-side od-switch-offices">
            <div className="od-switch-where" id="odSwitchWhere">
              {shownName}
            </div>
            <div className="od-switch-list" id="odSwitchOffices">
              {/* "All Offices" e mereu primul: e alegerea pe care o face omul de
                  noua ori din zece, si trebuie sa fie sub deget fara sa citeasca
                  lista. */}
              <button
                type="button"
                className={'od-switch-row od-switch-office' + (here && officeNow < 0 ? ' is-on' : '')}
                data-company={shown}
                data-office={ALL_OFFICES}
                onClick={() => go(shown, ALL_OFFICES)}
              >
                <span className="od-switch-name">All Offices</span>
              </button>
              {offices.map((office) => (
                <button
                  type="button"
                  key={office.id}
                  className={'od-switch-row od-switch-office' + (here && office.id === officeNow ? ' is-on' : '')}
                  data-company={shown}
                  data-office={office.id}
                  onClick={() => go(shown, office.id)}
                >
                  <span className="od-switch-name">{office.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="od-switch-foot bg-module">
          {/* Bifa are acelasi id ca in portal: pe ea sta intrebarea "ma uit la
              un client sau la toti" - vezi `companies_in_view()` si
              docs/izolare-firme.md. Se aplica pe loc, pe clientul si sediul de
              acum. */}
          {manyCompanies && (
            <label className="od-switch-all">
              <input
                type="checkbox"
                id="all_companies_selected"
                name="all_companies_selected"
                checked={shell.allCompanies}
                onChange={(e) => void changeCompany(current, officeNow, e.target.checked)}
              />{' '}
              All companies
            </label>
          )}
          <button id="close-change-company" type="button" className="btn btn-sm btn-inverse" onClick={ddClose}>
            Close
          </button>
        </div>
      </div>
    </li>
  )
}

/**
 * Hub-urile de setari care au ecran v4 (Application\Services\V4\SettingsCut::PAGES).
 * Restul raman in portalul vechi, deci se scriu fara `/app`. Se tine la zi
 * odata cu lista aceea - un hub taiat si nescris aici trimite omul inapoi in
 * CodeIgniter, care il intoarce cu 302 tot in /app.
 */
export const V4_SETTINGS = [
  'inventoree',
  'softsee',
  'opteemum',
  'proactivitee',
  'diagnostic_reports',
  /* surorile Softsee, 13 septembrie 2026: `securiteeSettingsIndexRoute`, `printeengSettingsIndexRoute` */
  'securitee',
  'printeeng',
  /* partea a doua a surorilor, 13 septembrie 2026: `protecteeSettingsIndexRoute` */
  'protectee',
  'saveenergee',
  'productivitee',
  /* mutate pe 9 septembrie 2026 - vezi `v4/web/src/settings/` */
  'general',
  'companies',
  'contacts',
  'licenses',
  'change_log',
  'download_agent',
  'rustdesk',
  /* 7 octombrie 2026: setarile furnizorului, sub RustDesk in General. */
  'rustdesk_servers',
  'install_options',
  /* Connection Quality faza 5 - modul nou, numai in v4. */
  'connection_quality',
  'floor_plans',
  'remote_connect',
  'api_integration',
  /* 12 octombrie 2026: webhooks de iesire, numai in v4. */
  'webhooks',
  /*
   * Adaugate pe 10 septembrie 2026. Lista statea neatinsa de o zi, iar intre
   * timp au plecat noua familii de ecrane (26.9.10.1): rotita trimitea mai
   * departe la un portal care nu mai e pe server.
   *
   * Cel mai des simtit: `easeedesk`. Setarile lui Adam Intop stau la
   * `settings/easeedesk/adam_intop_*`, iar `settingsUrl()` le reduce la hub-ul
   * `easeedesk` - care nu era aici, deci si randul de modul, si Adam Intop
   * plecau pe adresa veche.
   *
   * FIECARE nume de aici are ruta lui in `router.tsx`, verificata; cele trei
   * care n-au pagina de hub isi iau tinta din `MOVED` (vezi acolo).
   * `settingsHubs.test.ts` nu mai lasa lista sa ramana in urma rutelor.
   */
  'easeedesk',
  'easeetask',
  'user_accounts',
  'users_accounts',
  'authentication',
  'pass_safe',
  'company_reports',
  'customers',
  'customer_services',
  'customersV2',
  'contracts_templates',
  'agent_releases',
  'ai_agent',
  /*
   * IT Audit: catalogul si pagina pe tip sunt in /app de pe 12 septembrie
   * 2026 (`auditIndexRoute`, `auditTypeRoute`). Intrarea din rotita
   * (`settings/audit`, din `SettingsMenu`) duce aici, nu in portalul vechi.
   */
  'audit',
  /*
   * `ci_rules` are ruta lui (`/settings/ci_rules`) si randul lui in rail-ul
   * zonei Policies. Fara el aici, si rotita si fila din setarile Inventoree ar
   * fi plecat spre portalul care nu mai e pe server.
   */
  'ci_rules',
  /*
   * Celelalte patru intrari de descoperire din rail-ul Policies. Ecranele
   * erau mutate de mult, ca file ale setarilor Softsee, dar rail-ul le scrie
   * cu adresele portalului (`settings/telemetry`, `settings/discovery_setup`,
   * `settings/vault`, `settings/scan_rules`) - si fara hub aici plecau spre
   * portalul care nu mai e. Rutele lor sunt in `router.tsx`, langa Softsee.
   */
  'telemetry',
  'discovery_setup',
  'vault',
  'scan_rules',
  /* Service Catalog (Policies): `catalogSettingsRoute`, 12 septembrie 2026. */
  'catalog',
  /* Policies > Alarms: tinta e in `MOVED` (editorul de alarme, sub rail-ul Policies). */
  'alarms_new',
  /* Policies > Agent Autonomy: `autonomySettingsRoute`. Ultima intrare a rail-ului. */
  'autonomy',
  /*
   * Easeedesk > AI Analizer Actions (`settings/diagnostic/...`, controllerul
   * vechi `settings/Diagnostic`). Hub-ul n-are pagina proprie: tinta din
   * `MOVED` e prima lui intrare din bara, Quick Diagnostic Actions.
   */
  'diagnostic',
]

/**
 * Adresa unui ecran de setari scris ca in portal (`settings/pass_safe/index`).
 *
 * Hub-urile din V4_SETTINGS duc in /app, si adresa EXACTA o da tot `MOVED` -
 * `settings/diagnostic_reports` nu are ecran propriu, ci se deschide pe prima
 * lui fila (`/computer_scan`). Restul raman adrese de portal, fiindca acolo
 * chiar traiesc ecranele.
 */
export function settingsUrl(path: string): string {
  /*
   * INTAI ADRESA INTREAGA, ABIA APOI HUB-UL.
   *
   * Reducerea la hub e buna pentru cele mai multe ecrane, dar nu pentru toate:
   * `settings/ai_agent/company` si `settings/ai_agent` sunt doua pagini
   * diferite, iar `services_providers/service_providers` nu incepe deloc cu
   * `settings/`, deci n-are hub si cadea pe adresa portalului. Scrise intregi
   * in `MOVED`, ajung fix unde trebuie; restul reduc mai departe la hub, ca
   * pana acum.
   */
  const exact = MOVED[path]
  if (exact !== undefined) {
    return '/app' + exact
  }

  const hub = path.startsWith('settings/') ? (path.slice('settings/'.length).split('/')[0] ?? '') : ''

  /*
   * Pentru un hub MUTAT se scrie direct adresa din /app.
   *
   * Pana acum trecea prin `navUrl`, care cauta hub-ul in tabelul `MOVED` -
   * iar acolo sunt scrise paginile de modul, nu si ecranele de setari. Nu-l
   * gasea, si cadea pe adresa portalului vechi. Adica TOATE intrarile din
   * rotita duceau in /settings, chiar si cele rescrise, care traiau de-atunci
   * degeaba in /app.
   *
   * `V4_SETTINGS` e lista lor si e singura sursa de adevar de aici incolo:
   * cine muta un hub de setari il scrie acolo si atat.
   */
  if (hub === '' || !V4_SETTINGS.includes(hub)) {
    return portalUrl(path)
  }

  /* `MOVED` intai: unele hub-uri au acolo o tinta mai adanca decat hub-ul
     (diagnostic_reports duce la prima lui fila, nu la o pagina goala). */
  const moved = MOVED['settings/' + hub]

  /*
   * UN HUB FARA PAGINA PROPRIE NU-SI INGHITE ECRANELE.
   *
   * Reducerea la hub e buna acolo unde hub-ul CHIAR e o pagina: `settings/
   * pass_safe/index` si `settings/pass_safe` sunt acelasi ecran. Pentru
   * hub-urile care n-au pagina de hub - cele scrise in `MOVED` cu o tinta -
   * nu e: acolo tinta e PRIMA FILA, si reducerea trimitea orice ecran de sub
   * hub fix in fila aia.
   *
   * Asa ajungeau cele sase ecrane Adam Intop (`settings/easeedesk/
   * adam_intop_settings` si celelalte) sa deschida Ticket Types. Nu se vedea
   * ca o legatura rupta - se vedea ca o legatura care duce in alta parte,
   * adica exact felul de greseala pe care omul o pune pe seama lui.
   *
   * Aceeasi patanie ar fi avut-o toate cele 56 de ecrane de setari Easeedesk,
   * cele trei de sub `company_reports` si filele lui `diagnostic_reports`.
   * De-aia se repara REGULA, nu se scriu 60 de randuri in `MOVED`.
   *
   * Bara de sub hub ramane a hub-ului: adresa ceruta e a portalului, litera cu
   * litera, si tot litera cu litera e si ruta din `router.tsx` (vezi acolo,
   * la cele sase rute Adam Intop).
   */
  if (moved !== undefined && path !== 'settings/' + hub) {
    return '/app/' + path
  }

  return '/app' + (moved ?? '/settings/' + hub)
}

/**
 * Are adresa asta de setari un ecran in aplicatia asta?
 *
 * `settingsUrl()` raspunde deja, dar cu o adresa: ce e mutat iese sub `/app`,
 * ce nu, sub adresa portalului. Intrebarea se pune pentru randurile pe care nu
 * le scrie nimeni in cod - modulele firmei, citite din `it_modules` - unde
 * altfel s-ar desena o legatura catre un portal care nu mai e pe server.
 *
 * Se intreaba adresa, nu lista: `V4_SETTINGS` ramane singurul loc in care se
 * scrie ca un hub s-a mutat, si tot ce tine de `MOVED` (tinte mai adanci, filele
 * hub-urilor fara pagina proprie) e deja socotit inauntru.
 */
export function settingsMoved(path: string): boolean {
  return settingsUrl(path).startsWith('/app/')
}

/**
 * Sertarul rotitei, cu markupul din `_partials/settings_dropdown.php`:
 * capul cu chipul si numele, contul, codul de acces, cele doua coloane
 * (modulele si zonele), si subsolul cu Agent Install si Sign Out.
 *
 * Ce se vede si ce nu SE HOTARASTE PE SERVER (`/api/v2/settings/drawer`, vezi
 * `App\Settings\Drawer\SettingsDrawer`), ca la Settings > Inventoree: fiecare
 * rand din macheta veche avea o poarta, iar o poarta desenata in browser nu e
 * o poarta. Aici se deseneaza ce a venit, si nimic altceva.
 *
 * Lista se cere abia la prima deschidere (`enabled: open`): un chip de om si o
 * plimbare prin lista de setari la fiecare pagina, pentru un meniu pe care
 * nimeni nu l-a deschis, sunt platite degeaba.
 */
/* Randurile din "In General" care stau in coloana din dreapta a rotitei. */
const RIGHT_GENERAL = new Set(['reports', 'dynamic_reports'])

export function SettingsDropdown() {
  const { ref: ddRef, open: ddOpen, toggle: ddToggle, close: ddClose } = useDropdown()
  const [codeOpen, setCodeOpen] = useState(false)
  const [changeOpen, setChangeOpen] = useState(false)
  const drawer = useQuery({ ...settingsDrawerQuery(), enabled: ddOpen })
  const data = drawer.data
  /* Modulele cu ecran de setari in v4 (vezi comentariul de sub "Modules
     Settings"). Fara niciunul, coloana din stanga nu se deseneaza deloc si
     dreapta ia toata latimea - nu un titlu singur deasupra unui gol. */
  const modules = data ? data.modules.filter((m) => settingsMoved('settings/' + m.slug)) : []
  /* "In General" se imparte (13 octombrie 2026): Reports si Report builder
     stau in dreapta, sub Change Proposals; Auth Vault si Audit raman in
     stanga, sub module. Tot ce a trimis serverul, in ordinea lui - doar
     locul difera. Un grup fara randuri nu se deseneaza, nici titlul lui. */
  const general = data ? data.general : []
  const generalLeft = general.filter((g) => !RIGHT_GENERAL.has(g.id))
  const generalRight = general.filter((g) => RIGHT_GENERAL.has(g.id))
  const leftColumn = modules.length > 0 || generalLeft.length > 0
  const generalRow = (item: (typeof general)[number]) => (
    <li key={item.id}>
      <a
        className="pl-1"
        href={settingsUrl(item.url)}
        style={item.color !== '' ? { color: item.color } : undefined}
      >
        <i className={item.icon} aria-hidden="true"></i> {item.label}
        <span className="od-zone-hint">{item.hint}</span>
      </a>
    </li>
  )

  return (
    <li className={ddOpen ? 'dropdown open' : 'dropdown'} ref={ddRef}>
      <a
        href="#"
        title="Settings"
        data-toggle="dropdown"
        onClick={(e) => {
          e.preventDefault()
          ddToggle()
        }}
      >
        <i className="flaticon-cogwheel-2 flaticon-24"></i>
      </a>
      {/* `id`, nu `class`: toate regulile sertarului (od_ux.css, settings_console.css,
          components/_header.scss) pornesc de la `#settings-dropdown`. */}
      <div id="settings-dropdown" className="dropdown-menu dropdown-menu-right p-0">
        {/* Continutul se deruleaza intr-un invelis AL LUI, nu in sertar: sertarul
            poarta sageata de sus (`:before`, top -9px, app.css), si un
            `overflow` pe el ar taia-o. Inaltimea si derularea - in
            settings_console.css, sectiunea 21 (ecranele scunde, 1024x768). */}
        {data && (
          <div className="od-dd-scroll">
            <div className="dropdown-menu-header bg-module settings-account-head">
              <div className="user-avatar">
                {data.account.avatar !== '' && (
                  <img
                    className="imgiconavatar"
                    src={data.account.avatar}
                    alt=""
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                )}
              </div>
              <div className="settings-account-who">
                <h6>{data.account.name}</h6>
                <span>{data.account.department}</span>
              </div>
            </div>

            <div className="settings-account-row">
              {data.account.myAccount !== null && (
                <a className="dropdown-item settings-account-alone" href={settingsUrl(data.account.myAccount)}>
                  <i className="fas fa-user"></i> My Account
                </a>
              )}
            </div>

            <div className="settings-account-row">
              <a
                className="dropdown-item"
                id="openAccessCodeModal"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  ddClose()
                  setCodeOpen(true)
                }}
              >
                <i className="fa fa-fingerprint"></i> Enter Access Code
              </a>
            </div>

            <div className="dropdown-menu-body p-2">
              <div className="row">
                {leftColumn && (
                  <div className="col-md-6">
                    {/* Lista vine din modulele firmei, filtrata pe server cu
                        `settings.<modul>.read`; `settings` si `overview` nu sunt
                        in ea - vezi SettingsDrawer::modules(). */}
                    {modules.length > 0 && (
                    <ul className="sub-menu">
                      <li>
                        <h3>Modules Settings</h3>
                      </li>
                      {/*
                        SI UN MODUL FARA ECRAN DE SETARI IN v4 NU SE DESENEAZA.

                        Randurile astea nu sunt scrise nicaieri in cod: numele si
                        slug-ul vin din `it_modules`, deci lista lor e a BAZEI DE
                        DATE, nu a noastra. Fiecare server are alte module - unul
                        are Adam, altul Eventee - si pentru fiecare se scria
                        `settings/<slug>`, care fara ecran in v4 iesea in portalul
                        vechi. Portalul nu mai e pe server, deci era o usa zidita.

                        Nu se inventeaza o tinta ca sa mearga legatura (regula din
                        `SettingsCut`: "Do not invent a settings screen to make a
                        dead link work - remove the link"), si nu se scrie o a doua
                        lista de module in PHP, care ar ramane in urma lui
                        `V4_SETTINGS`. Se intreaba chiar adresa: daca `settingsUrl`
                        n-o scoate sub `/app`, ecranul nu exista in aplicatia asta
                        si randul lipseste - ca Overview, taiat mai devreme pe
                        server din acelasi motiv.

                        Poarta ramane a serverului (`settings.<modul>.read`); asta
                        nu e o poarta, ci intrebarea "stie aplicatia adresa?", la
                        care raspunde numai pachetul din browser, unde sta si
                        router-ul.
                      */}
                      {modules.map((m) => (
                        <li key={m.slug}>
                          <a className="" href={settingsUrl('settings/' + m.slug)}>
                            <i className="fas fa-angle-double-right"></i> {m.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                    )}

                    {/* Auth Vault si Audit, sub module, cum au fost mereu. */}
                    {generalLeft.length > 0 && (
                      <ul className="sub-menu">
                        <li>
                          <h3>In General</h3>
                        </li>
                        {generalLeft.map(generalRow)}
                      </ul>
                    )}
                  </div>
                )}
                <div className={leftColumn ? 'col-md-6' : 'col-md-12'}>
                  {/* Cele trei zone, ca trei usi - nu toata lista dinauntrul
                      lor: fiecare duce la primul ei ecran vizibil, si de acolo
                      rail-ul le are pe toate. */}
                  <ul className="sub-menu">
                    <li>
                      <h3>Settings</h3>
                    </li>
                    {data.zones.map((zone) => (
                      <li key={zone.key}>
                        <a className="pl-1" href={settingsUrl(zone.url)}>
                          <i className="fas fa-angle-double-right" aria-hidden="true"></i> {zone.label}
                          <span className="od-zone-hint">{zone.hint}</span>
                        </a>
                      </li>
                    ))}
                    <li>
                      <a className="pl-1" href="/app/settings">
                        <i className="fas fa-th-list" aria-hidden="true"></i> All settings
                        <span className="od-zone-hint">Every screen, with what it does</span>
                      </a>
                    </li>
                  </ul>

                  {data.changeProposals && (
                    <ul className="sub-menu">
                      <li>
                        {/* Sertarul din dreapta, ca in portal (`od-change-open`);
                            adresa ramane pagina intreaga, pentru Ctrl+clic. */}
                        <a
                          className="pl-1 od-change-open"
                          href={navUrl('change')}
                          onClick={(e) => {
                            e.preventDefault()
                            ddClose()
                            setChangeOpen(true)
                          }}
                        >
                          <i className="fas fa-robot" aria-hidden="true"></i> Change Proposals
                        </a>
                      </li>
                    </ul>
                  )}

                  {/* Reports si Report builder sub Change Proposals, ca randuri
                      ale coloanei, fara titlu: si Change Proposals sta asa, un
                      `ul` fara `h3`. Latimea: settings_console.css, sectiunea 25. */}
                  {generalRight.length > 0 && (
                    <ul className="sub-menu">{generalRight.map(generalRow)}</ul>
                  )}
                </div>
              </div>

              {/* Pagina de instalare a agentului si iesirea din cont: sub cele
                  doua coloane si despartite de o linie, fiindca niciuna nu e o
                  setare. Stilul e scris pe element, ca in macheta veche:
                  app.css se construieste cu laravel-mix, si pentru un buton nu
                  merita o reconstruire. */}
              <div className="row">
                <div className="col-md-12">
                  <div style={{ borderTop: '1px solid rgba(0,0,0,.08)', marginTop: 6, paddingTop: 10 }}>
                    <a
                      title="Agent Install"
                      href="/app/settings/download_agent"
                      style={{
                        display: 'block',
                        backgroundColor: '#f44f4f',
                        color: '#FFF',
                        borderRadius: 3,
                        padding: 8,
                        textAlign: 'center',
                        textDecoration: 'none',
                      }}
                    >
                      <i className="flaticon-multimedia" aria-hidden="true"></i> Agent Install
                    </a>
                    {/*
                      Iesirea se face pe capatul lui v4, nu pe `user/sign_out`
                      din portal: portalul vechi nu mai sta pe server, deci
                      adresa aia nu mai duce nicaieri si butonul nu facea nimic.

                      Ramane `<a href>` cu o adresa adevarata dinadins: cine
                      deschide in fila noua, sau are javascriptul oprit, ajunge
                      tot la ecranul de intrare - unde cookie-ul expirat il
                      scoate oricum. Apasarea obisnuita trece prin `onClick`,
                      care sterge cookie-ul intai (`api/client.ts: signOut`) si
                      tine bara de inactivitate stinsa pana pleaca pagina.
                    */}
                    <a
                      className="color-danger dropdown-item"
                      href="/app/login.html"
                      style={{ marginTop: 8, textAlign: 'center' }}
                      onClick={(e) => {
                        e.preventDefault()
                        void signOut()
                      }}
                    >
                      <i className="fas fa-sign-out-alt"></i> Sign Out
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <AccessCodeModal open={codeOpen} onClose={() => setCodeOpen(false)} />
      <ChangeDrawer open={changeOpen} onClose={() => setChangeOpen(false)} />
    </li>
  )
}

/*
 * Patratelul Settings nu-si ia tinta din `it_modules.url`.
 *
 * Toate celelalte patratele o iau de acolo, prin `navUrl()`. Al Setarilor a
 * incercat la fel si n-a mers pe server: valoarea din baza nu e cea din
 * inregistrarea de teste, si nimeni de aici nu poate sti ce scrie in fiecare
 * baza. Slug-ul insa e sigur - vine din numele modulului, coborat la litere
 * mici, si chiar pe el sta si dreptul de acces (`canAccess('settings')`).
 * Tinta e cea din `MOVED.settings`: prima fila, General.
 */
const SETTINGS_TILE = '/app' + MOVED.settings

function HeaderModules({ shell, reveal }: { shell: ShellData; reveal: ReturnType<typeof useHeaderNavReveal> }) {
  return (
    <div
      id="header-modules"
      onMouseEnter={reveal.showModules}
      onMouseMove={reveal.showModules}
      onMouseLeave={reveal.hideModulesSoon}
      onClick={(e) => e.stopPropagation()}
    >
      <ul className="header-menu">
        {shell.modules.map((m) => (
          <li className="modul-tip" data-s={m.slug} key={m.slug}>
            <a
              style={{ backgroundImage: `url(${assetUrl(`images/header/${m.icon}`)})` }}
              href={m.slug === 'settings' ? SETTINGS_TILE : navUrl(m.url)}
              title={m.name}
              className="tool-tip"
            >
              <p>{m.name}</p>
            </a>
            <span className="module-tagline">{m.tagline}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Insignele din dreapta subsolului, ca `_layout/commands_badges.php`.
 *
 * SASE, in ordinea din `Application\Services\Work\Badges::ALL` - aceeasi lista
 * pe care o citesc si macheta veche, si filele ferestrei "In flight". Erau
 * patru aici: lipseau Changes si Tickets, adaugate in portal pe 4 septembrie
 * 2026 odata cu `Services\Work\Inflight`.
 *
 * `badge-default` (Tickets) nu are varianta `.badge-transparent` in `app.css` -
 * si nu are nici in portal. Ramane pe stilul de baza `.badge`, la fel in
 * amandoua locurile; nu se inventeaza una aici.
 *
 * NUMERELE SUNT INCA ZERO. In portal le scrie `public/js/footer_commands.js`,
 * din `sidebar/remote_control/inflight`; v4 nu are inca acel cap si nici
 * scriptul. Vezi comentariul de la markup.
 */

/*
 * `href="#"` cu `preventDefault`, nu `javascript:void(0)` ca in portal.
 *
 * React 19 refuza o adresa `javascript:` intr-un `href` de JSX, iar jsdom
 * incearca sa o evalueze pe un ceas - deci fiecare randare a cojii intr-o
 * proba arunca o exceptie in afara probei. Probele treceau toate, si livrarea
 * se oprea oricum, pe unsprezece „unhandled errors".
 *
 * Nu se schimba nimic la vedere: foile si scripturile portalului se agata de
 * clase si de id-uri, nu de adresa.
 */
/**
 * ECRANELE DE SETARI CARE NU POARTA COLOANA DIN STANGA.
 *
 * -----------------------------------------------------------------------------
 * ASTA E O ABATERE DE LA PORTAL, CERUTA PE NUME
 * -----------------------------------------------------------------------------
 *
 * Cine pune cele doua ecrane alaturi va vedea ca portalul DESENEAZA rail-ul pe
 * `settings/change_log`, si aici nu. Nu e o scapare la portare, si nu e nevoie
 * sa fie reparata.
 *
 * Pe 10 septembrie 2026, Roxana Sima a cerut-o de doua ori, a doua oara
 * aratand chiar adresa: „ti am zis aici
 * https://optimumdesk.ai/app/settings/change_log sa scoti meniul din stanga,
 * n are ce cauta acolo". Jurnalul de livrari e o pagina la care omul se uita,
 * citeste si pleaca - nu un loc din care umbla mai departe prin setari, care e
 * tot rostul rail-ului. Prima data i s-a raspuns ca portalul il are si ca deci
 * ramane; hotararea nu era a celui care raspundea.
 *
 * -----------------------------------------------------------------------------
 * DE CE TOCMAI AICI, SI DE CE NU POATE SCAPA PE ALT ECRAN
 * -----------------------------------------------------------------------------
 *
 * Rail-ul se putea stinge din trei locuri, si doua dintre ele ar fi luat cu ele
 * mai mult decat s-a cerut:
 *
 *   - din `SettingsMenu` (scos randul, sau schimbat `match`) - dar atunci pleca
 *     si randul din sertarul rotitei, si intrarea aprinsa, si registrul de
 *     drepturi, care citesc TOATE aceeasi lista. S-a cerut o coloana scoasa, nu
 *     un ecran ascuns. Randul din sertar ramane neatins.
 *   - din `App\Settings\Rail\SettingsRail` (zona intoarsa goala) - acolo ar fi
 *     fost o exceptie ingropata intr-o clasa care raspunde la ORICE adresa, si
 *     care mai e chemata si pentru randul aprins.
 *
 * Locul ales e chiar cel in care se hotaraste ce se DESENEAZA, adica exact ce
 * s-a cerut. Si e o lista de adrese INTREGI, comparate cu `has()` - egalitate,
 * nu prefix. Un prefix ar fi stins tacut rail-ul pe orice ecran viitor scris
 * sub `settings/change_log/...`; egalitatea nu poate atinge decat adresele
 * scrise aici, una cate una. Serverul raspunde mai departe ce raspundea: cine
 * intreaba `/settings/rail` pentru adresa asta primeste tot zona ei.
 *
 * `change_log` - usa scurta, cea din subsol - e in lista desi serverul ii
 * raspunde oricum cu zona goala. Scris, ramane scris: daca maine cineva inscrie
 * jurnalul in `SettingsMenu` si cu adresa scurta, subsolul nu se strica.
 *
 * -----------------------------------------------------------------------------
 * SI CE RAMANE IN LOCUL EI - MASURAT, NU BANUIT
 * -----------------------------------------------------------------------------
 *
 * Grija era ca ecranul sa nu ramana cu un gol in stanga: `settings_console.css`
 * (sectiunea 16) impinge continutul cu `body.settings .od-zone-body`, si daca
 * invelisul ala ar fi ramas fara rail langa el, pagina ar fi stat trasa la
 * dreapta degeaba.
 *
 * Nu ramane. Invelisul nu e al machetei, ci al lui `SettingsZone`, care il scrie
 * NUMAI cand chiar are o zona de desenat - deci se duce odata cu coloana.
 * Masurat in Edge, cu foile adevarate din `public/css/`, pe latimea de 1440: cu
 * rail, continutul incepea la 84px si era lat de 1336 (rail-ul e strans la 64,
 * nu la 270 - vezi sectiunea 21); fara el incepe la 20px, adica chiar marginea
 * paginii, si e lat de 1400. La fel la 1920, 1280 si 1024, si nicaieri nu apare
 * vreo bara de derulare pe orizontala.
 */
const RAILLESS = new Set(['settings/change_log', 'change_log'])

/** Adresa e una din cele de mai sus? `/` de la coada nu face alta adresa. */
function railless(path: string): boolean {
  return RAILLESS.has(path.replace(/\/+$/, ''))
}

export function Shell() {
  const location = useLocation()
  const navigate = useNavigate()
  const bare = isBarePage(location.pathname)

  /* Ultima pagina din afara setarilor: acolo duce sigla din Settings. Se ia din
     `window.location`, care poarta si `/app`, si query-ul, si ancora. */
  useEffect(() => {
    rememberLocation(window.location.pathname, window.location.search, window.location.hash)
  }, [location.pathname, location.searchStr, location.hash])
  const inSettings = isSettingsPath(location.pathname)

  /* Modulul paginii e primul segment al adresei; bara de sub antet e a lui. */
  const module = moduleOf(location.pathname)
  /* `enabled: !bare` - vezi `barePages.ts`: pe o pagina fara sesiune cele doua
     intrebari raspund 401, si un 401 muta browserul la ecranul de intrare. */
  const shell = useQuery({ ...shellQuery(module), enabled: !bare })
  const todayCounts = useQuery({ ...todayCountsQuery(), enabled: !bare })

  /* Notele din bara din dreapta (`#my_memo`, `MyMemoBoard`); cifra de pe
     insigna vine din `getHeader()` in portal, aici din `GET /api/v2/memo/count`. */
  const [memoOpen, setMemoOpen] = useState(false)
  const memoBadge = useQuery({ queryKey: ['memo', 'count'], queryFn: memoCount, enabled: !bare, staleTime: 60_000 })
  const [memoNotes, setMemoNotes] = useState<number | null>(null)
  const memoShown = memoNotes ?? memoBadge.data?.notes ?? 0

  /*
   * Ce intrare din bara e a paginii de acum - ca general.js, dupa adresa.
   *
   * ADRESA INTREAGA, NU UN INCEPUT DE ADRESA. Regula portalului e scrisa in
   * `public/js/general.js`:
   *
   *     windowLocation.indexOf(href) >= 0
   *       && Math.abs(windowLocation.length - href.length) <= 1
   *
   * adica aceeasi adresa, cel mult cu o bara la coada - nu „incepe cu". Aici
   * statea si `currentHref.startsWith(i.href + '/')`, si din cauza lui
   * FIECARE ecran de sub o intrare din bara se purta ca si cum ar fi chiar
   * intrarea aia: intrarea se aprindea, `useHeaderNavReveal` socotea ca esti
   * pe ecranul ei si STRANGEA bara intreaga intr-un singur buton din antet.
   * In portal, aceleasi adrese arata bara deschisa, cu toate intrarile:
   * masurat in Edge pe 11 septembrie 2026, `/service_shop/requests` are
   * `#page-content` la y=100 (bara desfasurata) si nicio intrare aprinsa, iar
   * `/service_shop` - adresa intrarii - la y=50, cu „IT Service Catalog"
   * aprinsa. v4 le facea pe amandoua la y=50.
   *
   * Deci: potrivire pe adresa intreaga, cu bara de la coada iertata, ca in
   * portal. Nu atinge ecranele care SUNT adresa unei intrari - alea se aprind
   * si se string ca pana acum.
   */
  const currentHref = location.pathname.replace(/^\/app\//, '').replace(/^\//, '').replace(/\/+$/, '')
  /*
   * Bara de Settings scrie si modulele firmei (`settingsNav()`), iar un modul
   * fara hub in `V4_SETTINGS` n-are ecran de setari aici: legatura lui ar duce
   * in portalul care nu mai sta pe server. Aceeasi regula ca in sertarul
   * rotitei (`settingsMoved`, mai jos): ce n-are adresa in /app nu se deseneaza.
   */
  const nav = useMemo(() => {
    const entries = shell.data?.nav[module] ?? []
    return module === 'settings' ? entries.filter((e) => !isItem(e) || navUrl(e.href).startsWith('/app/')) : entries
  }, [shell.data, module])
  /*
   * Potrivire exacta pe `href`, apoi pe `prefixes` (bara de Settings: zona
   * General se aprinde pe `settings/users_accounts`, desi `href`-ul ei duce
   * la `settings/companies`) - vezi `selectedNavItem`.
   */
  const selected = selectedNavItem(nav, currentHref)

  const reveal = useHeaderNavReveal(selected !== null, location.pathname)

  /*
   * `od-console`, ca in `header.php` - vezi comentariul de la `odConsole`.
   * Se citeste ADRESA, nu doar modulul: jurnalul e singurul ecran de sub
   * `settings` pe care portalul nu-l trece pe desenul consolei.
   */
  const odConsole = module === 'settings' && !currentHref.startsWith('change_log')

  /* Clasele de pe <body>, ca in header.php: modulul, zona si bara laterala. */
  useEffect(() => {
    /* O pagina fara schelet nu poarta nici clasele lui: `app.css` imbraca dupa
       ele, si o tabla de perete isi duce singura toata foaia. */
    if (bare) return
    document.body.classList.add(module)
    if (odConsole) document.body.classList.add(OD_CONSOLE)
    if (SIDEBAR_READY && readCookie('user_sidebar') === 'visible') {
      document.body.classList.add('user-sidebar-active')
    }
    return () => document.body.classList.remove(module, OD_CONSOLE, 'user-sidebar-active')
  }, [module, odConsole, bare])

  useEffect(() => {
    /* Titlul si-l pune singura pagina fara schelet - o tabla de perete poarta
       numele ei, nu „OptimumDesk". */
    if (bare) return
    document.title = selected ? `${selected.label} - ${moduleName(module)} - OptimumDesk` : 'OptimumDesk'
  }, [selected, module, bare])

  /* Locurile listelor - vezi shellSlots.ts. */
  const [toolsClaims, setToolsClaims] = useState(0)
  const [headerTools, setHeaderTools] = useState<HTMLElement | null>(null)
  const [footerList, setFooterList] = useState<HTMLElement | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const claimHeaderTools = useCallback(() => {
    setToolsClaims((n) => n + 1)
    return () => setToolsClaims((n) => n - 1)
  }, [])
  const slots = useMemo<ShellSlots>(
    () => ({ claimHeaderTools, headerTools: toolsClaims > 0 ? headerTools : null, footerList, setTotal }),
    [claimHeaderTools, toolsClaims, headerTools, footerList],
  )

  const onNavClick = (item: NavItem) => (event: MouseEvent<HTMLAnchorElement>) => {
    /* Aceeasi adresa ca in `href` (`navUrl`), nu o a doua cautare in `MOVED`:
       o zona de Settings isi ia adresa din `V4_SETTINGS`, prin `settingsUrl`,
       si trebuie sa treaca tot prin router, ca celelalte intrari mutate. */
    const url = navUrl(item.href)
    if (!reveal.pinned) markNavigation()
    if (url.startsWith('/app/')) {
      event.preventDefault()
      void navigate({ to: url.slice('/app'.length) })
    }
  }

  const data = shell.data
  /* Implicit nimeni: pana vine scheletul, si cand campul lipseste, Adam nu apare. */
  const adamAccess = data?.adamAccess === true

  /* Insigna jurnalului. Se reciteste la fiecare schimbare de adresa, ca in
     portal, unde scriptul rula la fiecare incarcare de pagina: altfel, dupa ce
     omul deschide Change Log, numarul ar ramane pe ecran pana la un refresh. */
  const clUnseen = useMemo(
    () => changeLogUnseen(data?.changeLogVersions, data?.user.id ?? 0, location.pathname),
    [data?.changeLogVersions, data?.user.id, location.pathname],
  )

  /* Lista si motivele sunt in `barePages.ts`. */
  if (bare) {
    return <Outlet />
  }

  return (
    <ShellSlotsContext.Provider value={slots}>
      <AdamAccessContext.Provider value={adamAccess}>
      <div id="main-content">
        <div id="page-header">
          <header className="fixed-header">
            <ul className="page-logo">
              <li id="main-logo" className="hidden-md hidden-sm hidden-xs">
                {inSettings ? (
                  <a
                    href={settingsReturnTarget() ?? '/app/'}
                    id="main-logo-back"
                    onClick={(event) => {
                      event.preventDefault()
                      const target = settingsReturnTarget()
                      if (target !== null) void navigate({ href: target }) /* href public: routerul scoate singur `/app` (rewrite de basepath) */
                      else void navigate({ to: '/' })
                    }}
                  >
                    <img alt="Optimum Desk" src={assetUrl("images/logo/OptimumDeskHeader.png")} />
                  </a>
                ) : (
                  <img alt="Optimum Desk" src={assetUrl("images/logo/OptimumDeskHeader.png")} />
                )}
                <Adam size={18} />
              </li>
              <li id="module-logo">
                <span id="module-name">{moduleName(module)}</span>
              </li>
              <li id="odTodayItem">
                <a
                  href={navUrl('overview/today')}
                  id="odTodayLink"
                  title={todayTitle(todayCounts.data?.counts)}
                  onClick={(event) => {
                    event.preventDefault()
                    void navigate({ to: '/overview/today' })
                  }}
                >
                  <i className="fas fa-inbox" aria-hidden="true"></i>
                  <span id="odTodayBadge" className="od-today-badge" hidden={todayBadge(todayCounts.data?.counts) === ''}>
                    {todayBadge(todayCounts.data?.counts)}
                  </span>
                </a>
              </li>
              <li
                id="show-modules"
                onMouseEnter={reveal.showModules}
                onMouseMove={reveal.showModules}
                onMouseLeave={reveal.hideModulesSoon}
                onClick={(e) => {
                  e.stopPropagation()
                  document.body.classList.toggle('modules-shown')
                }}
              >
                <i className="fas fa-th"></i>
              </li>
              <li
                id="current-section"
                style={{ display: selected ? undefined : 'none' }}
                onMouseEnter={reveal.open}
                onMouseMove={reveal.open}
                onMouseLeave={reveal.scheduleClose}
                onClick={(e) => {
                  e.preventDefault()
                  reveal.togglePin()
                }}
              >
                {selected && (
                  <span
                    className="current-section-name"
                    role="button"
                    tabIndex={0}
                    aria-controls="page-header-nav"
                    aria-pressed={reveal.pinned ? 'true' : 'false'}
                    aria-expanded={reveal.pinned ? 'true' : 'false'}
                    title={reveal.pinned ? 'The menu stays open. Click to let it hide again.' : 'Click to keep the menu open'}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        reveal.togglePin()
                      }
                    }}
                  >
                    {selected.label}
                  </span>
                )}
              </li>
              {toolsClaims > 0 && <li className="tk-header-tools" ref={setHeaderTools}></li>}
            </ul>
            <ul id="header-right-nav">
              {data && <CompanyDropdown shell={data} />}
              <li>
                {/* Lupa deschide paleta (ui/SearchPalette.tsx) prin clasa `od-search-open`, ca `OdSearch` din od_ux.js. */}
                <a href="#" onClick={(e) => e.preventDefault()} className="od-search-open" title="Search everything (Ctrl+K)">
                  <i className="flaticon-search flaticon-20" aria-hidden="true"></i>
                </a>
              </li>
              <li className="space-divide"></li>
              <NotificationsBell />
              <li className="nav-quick-actions hidden-md hidden-sm hidden-xs">
                <a id="my_chat_btn" className="dropdown-toggle" title="Conversation" href="#" onClick={(e) => e.preventDefault()}>
                  <i className="flaticon-chat flaticon-20" aria-hidden="true"></i>
                  <span className="label hidden bg-warning font-10">0</span>
                </a>
              </li>
              <CollaborateBell />
              <li className="nav-quick-actions hidden-md hidden-sm hidden-xs">
                <a
                  id="my_memo"
                  title="Memo"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setMemoOpen((was) => !was)
                  }}
                >
                  <i className="flaticon-list-2 flaticon-20" aria-hidden="true"></i>
                  <MemoBadge count={memoShown} />
                </a>
              </li>
              {/* Agent Releases, intre Memo si rotita - ca in header.php. Numai
                  pentru cutiile postale din `can_manage_unix_agent_releases()`:
                  fara flag nu se scrie deloc (vezi AgentReleasesMark). */}
              {data?.canPublishAgents === true && <AgentReleasesMark />}
              {/* Contorul "Needs attention" (TodoMenu, fost "To do"), in locul iconitelor de sedii "Office N" si
                  de descoperire neconfigurata: o lista pe categorii, cu drumul la ecranul
                  fiecaruia - vezi GET /api/v2/shell/todo. */}
              {data && <TodoMenu />}
              {data?.settings && <SettingsDropdown />}
              {SIDEBAR_READY && (
                <li style={{ backgroundColor: '#00000012' }}>
                  <a
                    href="#"
                    id="user-sidebar-toogle"
                    title="Right Sidebar"
                    onClick={(e) => {
                      e.preventDefault()
                      const on = document.body.classList.toggle('user-sidebar-active')
                      document.cookie = `user_sidebar=${on ? 'visible' : 'hidden'}; path=/`
                    }}
                  >
                    <i className="flaticon-more-v4 flaticon-24"></i>
                  </a>
                </li>
              )}
            </ul>
          </header>
          {data && <HeaderModules shell={data} reveal={reveal} />}
          <ModuleBar
            activeKey={selected?.href}
            onMouseEnter={reveal.open}
            onMouseMove={reveal.open}
            onMouseLeave={reveal.scheduleClose}
          >
            {nav.map((entry, i) =>
              isSpace(entry) ? (
                <li className="menu-space" style={{ width: 44, height: 50 }} key={`space-${i}`}></li>
              ) : isFullscreen(entry) ? (
                <li className="pull-right" key="fullscreen">
                  <button
                    id="fulscreen"
                    className="btn btn-primary btn-xs"
                    title="Full Screen"
                    type="button"
                    onClick={() => void document.getElementById('page-content')?.requestFullscreen()}
                  >
                    <i className="fas fa-desktop"></i>
                  </button>
                </li>
              ) : (
                <li className={entry === selected ? 'selected' : undefined} key={entry.href}>
                  <a title={entry.title} href={navUrl(entry.href)} className={entry.cls} id={entry.id} onClick={onNavClick(entry)}>
                    {entry.badges?.map((b) => (
                      <span className={`label font-10 ${b.cls}`} title={b.title} key={b.mark}>
                        <span className="od-state-mark">{b.mark}</span>
                        {b.value.toLocaleString('en-US')}
                      </span>
                    ))}
                    {entry.label}
                    {(entry.badge > 0 || entry.badgeAlways) && (
                      <>
                        {entry.badgeAlways ? ' ' : ''}
                        <span className="label font-10">{entry.badge.toLocaleString('en-US')}</span>
                      </>
                    )}
                    {entry === selected && entry.badge <= 0 && !entry.badgeAlways && total !== null && total > 0 && (
                      <>
                        {' '}
                        <span className="label font-10 tk-total-badge">{total.toLocaleString('en-US')}</span>
                      </>
                    )}
                  </a>
                </li>
              ),
            )}
          </ModuleBar>
        </div>
        <div id="page-content">
          <div className="notifications"></div>
          {/*
            Rail-ul zonei de setari, scos din macheta si nu din fiecare ecran -
            ca in `header.php`. Asa il primesc si ecranele care n-au rail
            propriu (Authentication, Pass Safe), care altfel se deschideau fara
            nimic in stanga si fara drum inapoi.

            Afara de cele din `RAILLESS`, mai sus.
          */}
          {module === 'settings' && !railless(currentHref) ? (
            <SettingsZone path={currentHref}>
              {/*
                Si meniul setarilor unui MODUL, inauntrul rail-ului de zona.
                Cele doua nu se aseaza niciodata unul peste altul: meniul
                modulului se deseneaza doar pe adrese care nu sunt in nicio
                zona (`settings/easeedesk/...`), deci exact acolo unde rail-ul
                de deasupra se retrage si intoarce doar continutul.
              */}
              <EaseedeskSettingsMenu path={currentHref}>
                <Outlet />
              </EaseedeskSettingsMenu>
            </SettingsZone>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
      {bare ? <div id="user-sidebar"></div> : <UserSidebar />}
      <MemoBoard open={memoOpen} onClose={() => setMemoOpen(false)} onCount={setMemoNotes} />
      <div id="pageFooter">
        <div className="row">
          <div className="col-md-4 tk-footer-list" ref={setFooterList}></div>
          <div className="col-md-4 tk-footer-middle">
            <span className="copyright">
              <i className="far fa-copyright"></i> {new Date().getFullYear()} Optimum Desk - v{data?.version ?? ''}
            </span>
            {/*
              SPATIILE SE SCRIU, NU SE MOSTENESC.

              In macheta veche, intre `</span>` si `<a>` sta un rand nou, iar
              HTML il preface intr-un spatiu. JSX, in schimb, ARUNCA orice
              spatiu alb care cuprinde un rand nou intre doi frati - deci aici
              iesea „Change Log5Bug ReportFeature Request", lipit, ca in
              fotografia din 9 septembrie 2026.

              De-aia fiecare loc in care macheta are un rand nou primeste aici
              un `{' '}` scris de mana: si intre copyright si jurnal, si intre
              numele „Change Log" si insigna lui, si intre cele trei legaturi.
            */}
            {' '}
            {/*
              ADRESA SCURTA, CA IN PORTAL - `/change_log`, nu
              `/settings/change_log`.

              `_layout/footer.php` scrie `base_url() . 'change_log'`, si in
              blocul de birou, si in cel de telefon. Nu e o prescurtare: in
              portal cele doua adrese sunt doua ecrane scrise separat, si se
              deschid ALTFEL. Cea din rotita e o pagina de setari si poarta
              rail-ul zonei in stanga; cea scurta nu e, si nu-l poarta -
              `SettingsMenu` inregistreaza jurnalul numai sub
              `settings/change_log`, deci `zoneFor('change_log')` iese gol.

              Scrisa aici adresa rotitei, subsolul ducea oamenii pe ecranul CU
              rail. Un om care apasa "Change Log" din coltul paginii nu umbla
              prin setari - se uita ce s-a livrat, si apoi se intoarce la ce
              facea. Coloana de setari lipita in stanga il asaza intr-un loc in
              care nu ceruse sa intre.

              Cine chiar umbla prin setari ajunge din rotita, la adresa lunga,
              si acolo rail-ul ramane - ca in portal. Doua usi, doua forme,
              dinadins.
            */}
            <a href="/app/change_log" id="od-cl-foot" className="od-cl-foot">
              Change Log{' '}
              <span className="od-cl-badge" id="od-cl-badge" hidden={clUnseen === ''}>
                {clUnseen}
              </span>
            </a>{' '}
            {/* CLASA, nu stil in linie: culorile de pe bara inchisa se scriu cu
                `#pageFooter` in fata (vezi Shell.css, copiat din footer.php).
                Scrise in linie, erau cele de pe TELEFON (#f04a4a / #318cb0,
                facute pentru randul alb de la capatul paginii), nu cele urcate
                pentru fundalul #393F4C - si nici hover nu aveau. */}
            {/*
              SUB `/app`, DESI ECRANELE NU S-AU MUTAT INCA.
              Duceau la `/bug_reports` si `/feature_requests`, adica la
              portalul vechi - care nu mai sta pe server. Doua legaturi din
              subsolul FIECAREI pagini duceau intr-un gol.

              Adresa ramane cea din portal, doar mutata sub `/app`: asa incepe
              sa mearga in ziua in care ecranul soseste, fara sa umble nimeni
              din nou prin subsol. Pana atunci raspunde tot aplicatia noua, cu
              pagina ei de "nu exista" - un raspuns, nu o gaura.
            */}
            <a href="/app/bug_reports" id="bug_reports_link" className="od-page-foot-bug">
              Bug Report
            </a>{' '}
            <a href="/app/feature_requests" id="feature_requests_link" className="od-page-foot-req">
              Feature Request
            </a>
          </div>
          {/* Ce e in lucru chiar acum - insignele din `_layout/commands_badges.php`,
              cu numerele de la `GET /api/v2/sidebar/inflight` la 30 s
              (`footer_commands.js`); fiecare deschide fereastra pe fila ei. */}
          <div className="col-md-4 tk-footer-commands">
            {!bare && <InflightBadges />}
          </div>
        </div>
      </div>
      {/* "Ai fost deconectat pentru inactivitate" + codul pe mail, in josul
          paginii - `_layout/_idle_bar.php`. Ascunsa pana la primul 401 cu
          motivul `idle` (api/idle.ts). */}
      <IdleBar />
      {!bare && <InflightDrawer />}
      {/* Sertarul rapid din dreapta (`#app-footer .quick-sidebar-navigation`), pe orice pagina. */}
      {!bare && <QuickSidebar />}
      {!bare && <SearchPalette />}
      {/* Sertarul lui Adam, pe orice pagina - `_partials/od_ai_drawer.php`.
          NUMAI cui ii raspunde serverul (`adamAccess` din `/shell`): fara el
          nu se deseneaza nici sertarul, nici bula de pe bara de jos. */}
      {!bare && adamAccess && <AdamDrawer />}
      </AdamAccessContext.Provider>
    </ShellSlotsContext.Provider>
  )
}

/** Continutul unei pagini, ca `<div id="content">` din machetele vechi. */
export function Content({ children }: { children: ReactNode }) {
  return <div id="content">{children}</div>
}
